import { useEffect, useMemo, useState } from 'react'

import { useControllerConfigurationFileSetContext } from '@/features/controller/configuration/contexts/ControllerConfigurationFileSetContext'
import { useMultiModalConfiguration } from '@/features/controller/configuration/hooks/useMultiModalConfiguration'
import type {
  ControllerConfigurationModel,
  ControllerConfigurationModelUpdate,
  ControllerConfigurationSourceUpdate,
  LatestConfigurationVersions,
  UnsavedConfiguration,
} from '@/features/controller/configuration/models/controllerConfiguration'
import { isTillquistEnabled } from '@/features/controller/configuration/models/raspiProperties/tillquist'

type useEditableControllerConfigurationModelParams = {
  onNewPowerMeterAdded: () => void
}

type UnsavedChanges = {
  appJson?: UnsavedConfiguration
  raspiProperties?: UnsavedConfiguration
}

type UseEditableControllerConfigurationModel = {
  configurationModel: ControllerConfigurationModel
  isLoading: boolean
  latestConfigurationVersions: LatestConfigurationVersions
  onConfigurationModelUpdated: (update: ControllerConfigurationModelUpdate) => void
  onConfigurationSourceUpdated: (update: ControllerConfigurationSourceUpdate) => void
  discardUnsavedChanges: () => void
  persistUnsavedChanges: () => Promise<void>
  /** Exposes the source code as unsaved changes if there are any */
  unsavedChanges: { appJson?: UnsavedConfiguration; raspiProperties?: UnsavedConfiguration }
  /** Exposes the source code regardless if there are unsaved changes or not */
  sourceView: ControllerConfigurationSourceUpdate
}

function hasConfigurationModelTillquistEnabled(configurationModel: ControllerConfigurationModel) {
  return configurationModel.raspiPropertiesConfig
    ? isTillquistEnabled(configurationModel.raspiPropertiesConfig.tillquistConfig)
    : false
}

export function useEditableControllerConfigurationModel({
  onNewPowerMeterAdded,
}: useEditableControllerConfigurationModelParams): UseEditableControllerConfigurationModel {
  const { appJson, raspiProperties } = useControllerConfigurationFileSetContext()
  const latestConfigurationVersions: LatestConfigurationVersions = {
    appJson: appJson.latestVersion,
    raspiProperties: raspiProperties.latestVersion,
  }
  const isFetched = appJson.isLatestVersionFetched && raspiProperties.isLatestVersionFetched
  const isLoading = appJson.isLatestVersionLoading || raspiProperties.isLatestVersionLoading

  const { multiModalConfiguration, applyConfigurationModelUpdate, applyConfigurationSourceUpdate } =
    useMultiModalConfiguration({
      initialSource: isFetched
        ? {
            appJsonText: latestConfigurationVersions.appJson?.value,
            raspiPropertiesText: latestConfigurationVersions.raspiProperties?.value,
          }
        : {},
    })
  const { model: configurationModel, source: configurationSourceText } = multiModalConfiguration

  const [appJsonComment, setAppJsonComment] = useState<string>('')
  const [raspiPropertiesComment, setRaspiPropertiesComment] = useState<string>('')
  const [notifyIfTillquistMeterAdded, setNotifyIfTillquistMeterAdded] = useState(false)

  function readConfigurationStateFromSource() {
    const loadedModel = applyConfigurationSourceUpdate({
      appJsonText: latestConfigurationVersions.appJson?.value,
      raspiPropertiesText: latestConfigurationVersions.raspiProperties?.value,
    })
    // If we didn't previously have Tillquist configured, we should notify if it does get added.
    // This reads the model just loaded, not `configurationModel`, which is still the previous
    // render's value here and on the first load is the empty initial one -- which armed the
    // notification for every controller that already had Tillquist enabled.
    setNotifyIfTillquistMeterAdded(!hasConfigurationModelTillquistEnabled(loadedModel))
    // This handles the case where UI hasn't been used for any updates but there are still changes
    const comment = 'Configuration improvement suggested by FlexPortal'
    setAppJsonComment(comment)
    setRaspiPropertiesComment(comment)
  }

  useEffect(() => {
    if (isFetched) {
      readConfigurationStateFromSource()
    }
  }, [latestConfigurationVersions.appJson, latestConfigurationVersions.raspiProperties, isFetched])

  const { unsavedRaspiProperties, unsavedAppJson } = useMemo(() => {
    return {
      unsavedAppJson:
        configurationSourceText.appJsonText !== latestConfigurationVersions.appJson?.value
          ? configurationSourceText.appJsonText
          : undefined,
      unsavedRaspiProperties:
        configurationSourceText.raspiPropertiesText !== latestConfigurationVersions.raspiProperties?.value
          ? configurationSourceText.raspiPropertiesText
          : undefined,
    }
  }, [
    latestConfigurationVersions.appJson?.value,
    latestConfigurationVersions.raspiProperties?.value,
    configurationSourceText.appJsonText,
    configurationSourceText.raspiPropertiesText,
  ])

  function onConfigurationModelUpdated({
    updateAppConfig,
    updateRaspiPropertiesConfig,
    comment,
  }: ControllerConfigurationModelUpdate) {
    applyConfigurationModelUpdate({ updateAppConfig, updateRaspiPropertiesConfig })
    if (comment && updateAppConfig) {
      setAppJsonComment(comment)
    }
    if (comment && updateRaspiPropertiesConfig) {
      setRaspiPropertiesComment(comment)
    }
  }

  function onConfigurationSourceUpdated({
    appJsonText,
    comment,
    raspiPropertiesText,
  }: ControllerConfigurationSourceUpdate) {
    applyConfigurationSourceUpdate({ appJsonText, raspiPropertiesText })
    if (comment && appJsonText && appJsonText !== latestConfigurationVersions.appJson?.value) {
      setAppJsonComment(comment)
    }
    if (comment && raspiPropertiesText && raspiPropertiesText !== latestConfigurationVersions.raspiProperties?.value) {
      setRaspiPropertiesComment(comment)
    }
  }

  async function persistUnsavedChanges() {
    if (unsavedAppJson || unsavedRaspiProperties) {
      if (unsavedAppJson) {
        await appJson.updateAndInvalidate({
          previousVersionId: latestConfigurationVersions.appJson!.id,
          comment: appJsonComment,
          value: unsavedAppJson,
        })
      }
      if (unsavedRaspiProperties) {
        await raspiProperties.updateAndInvalidate({
          previousVersionId: latestConfigurationVersions.raspiProperties!.id,
          comment: raspiPropertiesComment,
          value: unsavedRaspiProperties,
        })
      }
      if (notifyIfTillquistMeterAdded) {
        const nowHasTillquistEnabled = hasConfigurationModelTillquistEnabled(configurationModel)
        if (nowHasTillquistEnabled) {
          onNewPowerMeterAdded()
        }
      }
    }
  }

  const sourceView: ControllerConfigurationSourceUpdate = {
    appJsonText: unsavedAppJson ?? latestConfigurationVersions.appJson?.value,
    raspiPropertiesText: unsavedRaspiProperties ?? latestConfigurationVersions.raspiProperties?.value,
  }

  const unsavedChanges: UnsavedChanges = {
    appJson: unsavedAppJson
      ? {
          value: unsavedAppJson,
          comment: appJsonComment,
          setComment: setAppJsonComment,
        }
      : undefined,
    raspiProperties: unsavedRaspiProperties
      ? {
          value: unsavedRaspiProperties,
          comment: raspiPropertiesComment,
          setComment: setRaspiPropertiesComment,
        }
      : undefined,
  }

  return {
    configurationModel,
    discardUnsavedChanges: readConfigurationStateFromSource,
    isLoading,
    latestConfigurationVersions,
    onConfigurationModelUpdated,
    onConfigurationSourceUpdated,
    persistUnsavedChanges,
    sourceView,
    unsavedChanges,
  }
}
